'use client';

import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { RepeatIcon } from '@/components/ui/icon/glyphs';
import { ModalFooter } from '@/components/ui/modal-footer';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { D6_SCALE } from '@/config/solana';
import { useOutcomeTokenSnapshot } from '@/hooks/gifts/use-outcome-token-snapshot';
import { useFormattedCurrency } from '@/lib/currency';
import { GiftHandleBadge } from './gift-handle-badge';
import { OutcomeTokenDisplay } from './outcome-token-display';
import { ShareGiftCta } from './share-gift-cta';

export interface GiftSuccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Permalink for the public gift page (`/gifts/<opaqueId>`). */
  permalinkUrl: string;
  /** Sanitised recipient handle (e.g. `@alice` or `@bob`). */
  recipientDisplay: string;
  /**
   * Recipient platform — drives the visible identity badge so senders can
   * distinguish `@cam_tutor` (Morfi) from `@morfimarkets` (X). Defaults to
   * `'morfi'` when omitted (back-compat).
   */
  recipientPlatform?: 'morfi' | 'x';
  /** Recipient handle without leading "@" — used by the visible identity badge. */
  recipientHandle?: string;
  /**
   * USDC base units (D6). For outcome-token gifts this is the user's USD-input
   * estimate at submit time — only used as a fallback for the share-text
   * dollar amount; the visible display uses the live outcome snapshot.
   */
  amountD6: string;
  /**
   * Gift asset kind. Defaults to `'usdc'` for back-compat.
   */
  assetKind?: 'usdc' | 'outcome_token';
  /**
   * Outcome-token mint pubkey (base58) — required when `assetKind === 'outcome_token'`.
   * Drives the `<OutcomeTokenDisplay>` live price + label + market lookup.
   */
  outcomeMintPubkey?: string | null;
  /**
   * Outcome token amount sent, in token base units (D6). Used by
   * `<OutcomeTokenDisplay>` to render the live USD value of the gifted shares.
   */
  outcomeAmountBaseUnits?: string;
  /**
   * Backend-supplied outcome label (e.g. "Yes") — fallback for share text
   * when the live snapshot hasn't resolved yet.
   */
  outcomeLabelFallback?: string | null;
  /**
   * Backend-supplied market title (e.g. "Will BTC hit 100k?") — fallback
   * for share text when the live snapshot hasn't resolved yet.
   */
  marketTitleFallback?: string | null;
  /** GiftDeposit pubkey for analytics correlation. */
  depositPubkey?: string;
  /** Triggered by "Send another" CTA. */
  onSendAnother?: () => void;
}

/**
 * Confirmation screen rendered after a successful gift submission (US-6).
 * Surfaces the unified <ShareGiftCta> share group + Send another action.
 * Currency animation uses `<AnimatedNumber />` per FR-070; share clicks emit
 * funnel events per FR-077.
 */
export function GiftSuccessModal(props: GiftSuccessModalProps): React.ReactNode {
  const t = useTranslations('gift.send.success');
  const { formatD6 } = useFormattedCurrency();

  const amountText = formatD6(props.amountD6, 'compact');
  const recipientPlatform = props.recipientPlatform ?? 'morfi';
  const recipientHandle = props.recipientHandle ?? props.recipientDisplay.replace(/^@/, '');
  const assetKind = props.assetKind ?? 'usdc';
  const isOutcome = assetKind === 'outcome_token';

  // Pull the live outcome snapshot for outcome-token gifts so the share-text
  // builder has the freshest label + market title. Falls back to the
  // backend-supplied props when the live snapshot is still loading.
  const snapshotMint = isOutcome ? (props.outcomeMintPubkey ?? null) : null;
  const snapshot = useOutcomeTokenSnapshot(snapshotMint);
  const liveOutcomeLabel = snapshot.outcomeLabel ?? props.outcomeLabelFallback ?? null;
  const liveMarketTitle = snapshot.marketTitle ?? props.marketTitleFallback ?? null;

  return (
    <SilkSheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      variant="detached"
      size="md"
      title={t('title')}
      description={
        isOutcome && liveOutcomeLabel !== null && liveMarketTitle !== null
          ? t('description_outcome', {
              recipientHandle: props.recipientDisplay,
              amount: amountText,
              outcomeLabel: liveOutcomeLabel,
              marketTitle: liveMarketTitle,
            })
          : t('description', {
              recipientHandle: props.recipientDisplay,
              amount: amountText,
            })
      }
      footer={
        <ModalFooter>
          <ShareGiftCta
            permalinkUrl={props.permalinkUrl}
            recipientDisplay={props.recipientDisplay}
            recipientPlatform={recipientPlatform}
            amountText={amountText}
            assetKind={assetKind}
            outcomeLabel={liveOutcomeLabel}
            marketTitle={liveMarketTitle}
            {...(props.depositPubkey !== undefined ? { depositPubkey: props.depositPubkey } : {})}
          />
          {props.onSendAnother !== undefined ? (
            <AnimatedButton type="button" prefix={RepeatIcon} onClick={props.onSendAnother}>
              {t('sendAnother')}
            </AnimatedButton>
          ) : null}
        </ModalFooter>
      }
    >
      <div className="flex flex-col items-center gap-2 py-4" aria-live="polite">
        <GiftHandleBadge platform={recipientPlatform} handle={recipientHandle} size="md" />
        {isOutcome &&
        props.outcomeMintPubkey !== undefined &&
        props.outcomeMintPubkey !== null &&
        props.outcomeAmountBaseUnits !== undefined ? (
          <OutcomeTokenDisplay
            mintPubkey={props.outcomeMintPubkey}
            amountBaseUnits={props.outcomeAmountBaseUnits}
            fallbackOutcomeLabel={props.outcomeLabelFallback ?? null}
            fallbackMarketTitle={props.marketTitleFallback ?? null}
          />
        ) : (
          <span className="text-3xl font-medium">
            <AnimatedNumber
              value={Number(props.amountD6) / D6_SCALE}
              format={(v: number): string => formatD6(String(Math.round(v * D6_SCALE)), 'full')}
              animateOnMount={true}
            />
          </span>
        )}
      </div>
    </SilkSheet>
  );
}
